import "chart.js/auto";
import { Box } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { blend } from "@mui/system";
import { Chart, ChartOptions, Plugin, Scale } from "chart.js";
import { ReactNode, useMemo, useRef } from "react";
import { Scatter } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { getTicks } from "../../../utils/chartjsTheming";
import { BAR_FILL_ALPHA, Tone } from "../../general/dataAtoms/shared.ts";
import { EmptyPanelNote } from "../EmptyPanelNote.tsx";
import { ChartHeaderStats, HeaderStat } from "./ChartHeaderStats.tsx";
import { ScatterTooltip } from "./ScatterTooltip.tsx";
import { SplitBar } from "./SplitBar.tsx";
import {
  normalisedYAxis,
  POINT_HOVER_RADIUS,
  SCATTER_HEIGHT,
  scatterDataset,
  XY,
} from "./scatterDataset.ts";
import { useScatterTooltip } from "./useScatterTooltip.ts";

// canvas padding above the plot, in px: room for the labels an overlay paints
// over its reference lines, or just a little air when it paints none
const ANNOTATED_TOP_PADDING_PX = 18;
const PLAIN_TOP_PADDING_PX = 8;

/** How far the ring around the dot under the pointer reaches past it, in px. */
const HOVER_RING_PX = 5;
/** How much of the colour of the text the ring takes. */
const HOVER_RING_ALPHA = 0.1;

/** The width of a reference line an overlay paints, in px. */
export const REFERENCE_LINE_WIDTH = 1.5;
/** The dashes of a reference line that is one of several, in px. */
export const REFERENCE_LINE_DASH = [4, 4];

export interface ZoneColor {
  // of the zone's part of the divided bar: see-through, like a bar
  fill: string;
  // the same fill on the colour of a card. A dot is filled with it, so that
  // the lines behind the dot do not show through
  dot: string;
  // the edge of a dot
  edge: string;
}

/** The colour of a zone from a colour of the palette and its dark shade. */
export const zoneColorOf = (
  theme: Theme,
  { main, dark }: { main: string; dark: string },
): ZoneColor => ({
  fill: alpha(main, BAR_FILL_ALPHA),
  dot: blend(theme.palette.background.paper, main, BAR_FILL_ALPHA),
  edge: dark,
});

/** The colour of a zone by what it says of the cases in it. */
export const toneZoneColors = (
  theme: Theme,
): { [tone in Tone]: ZoneColor } => ({
  favourable: zoneColorOf(theme, theme.palette.data.positive),
  // neither good nor bad: the cases settling left unchanged. Taken from the
  // greys every theme carries, a user's own included
  neutral: zoneColorOf(theme, {
    main: theme.palette.grey[600],
    dark: theme.palette.grey[700],
  }),
  adverse: zoneColorOf(theme, theme.palette.data.negative),
});

/** One coloured group of dots, and its part of the divided bar underneath. */
export interface ScatterZone<P> {
  // tells the zones of a chart apart
  key: string;
  points: readonly P[];
  color: ZoneColor;
  // what the part of the bar says: how many dots the zone has, and of what kind
  label: string;
}

export interface ScatterAxis {
  min: number;
  max: number;
  // fixed tick positions, in axis units; chart.js picks its own when omitted
  ticks?: readonly number[];
  formatTick: (value: number) => string;
  title?: string;
}

/** Paints reference lines and their labels behind the dots. */
export type ScatterOverlay = (chart: Chart<"scatter">) => void;

const fixedTicks = (ticks: readonly number[] | undefined) =>
  ticks
    ? (scale: Scale) => {
        scale.ticks = ticks.map((value) => ({ value }));
      }
    : undefined;

/**
 * The frame every settlement scatter shares: heading with headline stats, one
 * dot per case grouped into coloured zones, a React-rendered tooltip (so it can
 * hold MUI content), an overlay of reference lines, and a divided bar counting
 * the zones in the order given.
 *
 * Hovering a dot re-renders this component, and react-chartjs-2 calls
 * `chart.update()` whenever the identity of `options` or `data.datasets`
 * changes. Both are therefore memoised here — which only works if the caller
 * passes stable `zones`, `toXY`, `xAxis` and `yAxis` (memoise them, or keep
 * them at module level). `drawOverlay` and `renderTooltip` may be inline.
 */
export const ZonedScatter = <P extends { caseId: string; caseName: string }>({
  title,
  subtitle,
  ariaLabel,
  stats,
  zones,
  toXY,
  xAxis,
  yAxis,
  drawOverlay,
  overlayHasTopLabels = false,
  renderTooltip,
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  // says what the plot shows to those who cannot see it
  ariaLabel: string;
  stats?: readonly HeaderStat[];
  // drawn, and listed in the divided bar, in this order
  zones: readonly ScatterZone<P>[];
  toXY: (point: P) => XY;
  xAxis: ScatterAxis;
  // omitted: y is the claim value normalised 0…1, which only orders the dots,
  // so the axis shows its title but no ticks
  yAxis?: ScatterAxis;
  drawOverlay?: ScatterOverlay;
  overlayHasTopLabels?: boolean;
  // the body of the hover card, under the case name
  renderTooltip: (point: P) => ReactNode;
  // shown in place of the chart when there is nothing to plot
  emptyMessage: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement",
  });

  const pointsOfDataset = useMemo(
    () => zones.map(({ points }) => points),
    [zones],
  );
  const { tip, externalTooltip } = useScatterTooltip(pointsOfDataset);

  const data = useMemo(
    () => ({
      datasets: zones.map(({ points, color }) =>
        scatterDataset(points, toXY, color.dot, color.edge),
      ),
    }),
    [zones, toXY],
  );

  // react-chartjs-2 reads `plugins` once, when the chart is created, so a
  // plugin that captured this render's overlay or theme would go stale (on a
  // theme switch, say). The plugin stays the same object and looks both up
  // when it draws instead.
  const overlayRef = useRef(drawOverlay);
  overlayRef.current = drawOverlay;
  const ringRef = useRef("");
  ringRef.current = alpha(theme.palette.text.primary, HOVER_RING_ALPHA);
  const plugins = useMemo<Plugin<"scatter">[]>(
    () => [
      {
        id: "zonedScatterOverlay",
        beforeDatasetsDraw: (chart) => {
          overlayRef.current?.(chart);
          // a ring around the dot under the pointer, behind the dots
          const { ctx } = chart;
          ctx.save();
          ctx.fillStyle = ringRef.current;
          for (const { element } of chart.getActiveElements()) {
            ctx.beginPath();
            ctx.arc(
              element.x,
              element.y,
              POINT_HOVER_RADIUS + HOVER_RING_PX,
              0,
              2 * Math.PI,
            );
            ctx.fill();
          }
          ctx.restore();
        },
      },
    ],
    [],
  );

  const options = useMemo<ChartOptions<"scatter">>(() => {
    const { font } = getTicks(theme);
    const axisTitle = (text: string | undefined) => ({
      display: text !== undefined,
      text: text ?? "",
      color: theme.palette.text.secondary,
      font,
    });
    // the line of an axis is stronger than the grid it starts
    const border = { color: theme.palette.action.disabled };
    const linearAxis = (axis: ScatterAxis, isHorizontal: boolean) => ({
      type: "linear" as const,
      min: axis.min,
      max: axis.max,
      afterBuildTicks: fixedTicks(axis.ticks),
      ticks: {
        font,
        color: theme.palette.text.secondary,
        // the ends of the scale are no round amounts: named, they would
        // stand right beside the round ones next to them
        includeBounds: false,
        // a horizontal axis may drop labels, but never tilts them
        ...(isHorizontal ? { autoSkip: true, maxRotation: 0 } : {}),
        callback: (value: string | number) =>
          typeof value === "number" ? axis.formatTick(value) : value,
      },
      // the grid stays quiet throughout: the lines that matter, that of zero
      // among them, are the reference lines the overlay paints over it
      grid: { color: theme.palette.divider },
      border,
      title: axisTitle(axis.title),
    });
    return {
      maintainAspectRatio: false,
      // no animation: the tooltip drives a re-render on hover, and an animated
      // update would make the dots visibly jump
      animation: false,
      transitions: { active: { animation: { duration: 0 } } },
      layout: {
        padding: {
          top: overlayHasTopLabels
            ? ANNOTATED_TOP_PADDING_PX
            : PLAIN_TOP_PADDING_PX,
        },
      },
      plugins: {
        legend: { display: false },
        // the canvas tooltip is off; chart.js only reports the hovered dot
        tooltip: { enabled: false, external: externalTooltip },
      },
      scales: {
        x: linearAxis(xAxis, true),
        y: yAxis
          ? linearAxis(yAxis, false)
          : {
              ...normalisedYAxis,
              border,
              title: axisTitle(t("claimAxis")),
            },
      },
    };
  }, [theme, xAxis, yAxis, overlayHasTopLabels, externalTooltip, t]);

  if (zones.every(({ points }) => points.length === 0))
    return <EmptyPanelNote>{emptyMessage}</EmptyPanelNote>;

  return (
    // set off from what the card says of the chart above it
    <Box sx={{ mt: 1.75, pt: 1.75, borderTop: 1, borderColor: "divider" }}>
      <ChartHeaderStats title={title} subtitle={subtitle} stats={stats} />

      <Box
        sx={{
          position: "relative",
          height: SCATTER_HEIGHT,
          width: "100%",
          mt: 1.5,
        }}
      >
        <Scatter
          aria-label={ariaLabel}
          plugins={plugins}
          data={data}
          options={options}
        />
        {tip && (
          <ScatterTooltip
            canvas={tip.canvas}
            x={tip.x}
            y={tip.y}
            title={tip.point.caseName}
          >
            {renderTooltip(tip.point)}
          </ScatterTooltip>
        )}
      </Box>

      <SplitBar
        parts={zones.map(({ key, points, color, label }) => ({
          key,
          count: points.length,
          color: color.fill,
          label,
        }))}
      />
    </Box>
  );
};
